import axios from 'axios';
import router from '@/router';

const api = axios.create({
  baseURL: 'https://localhost:8081',
  timeout: 10000
});

// ========== 请求拦截 ===========
api.interceptors.request.use(config => {
  const access = localStorage.getItem('accessToken');
  const refresh = localStorage.getItem('refreshToken');

  // 是否需要 token（默认需要）
  const needAuth = config.noAuth ? false : true;

  // 未登录且需要鉴权 → 阻止请求
  if (needAuth && !access && !config.isRefresh) {
    return Promise.reject({ code: 401, message: "未登录" });
  }

  // 给需要的请求添加 accessToken
  if (access && !config.isRefresh) {
    config.headers['accessToken'] = access;
  }

  return config;
}, err => Promise.reject(err));

// 刷新 token 状态
let isRefreshing = false;
let subscribers = [];

function subscribe(cb) {
  subscribers.push(cb);
}

function notify(token) {
  subscribers.forEach(cb => cb(token));
  subscribers = [];
}

function clearTokensAndRedirect() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userInfo');
  
  // 跳转到登录页
  router.push({ name: 'Login' }).then(() => {
    window.location.reload();
  });
}

function refreshToken() {
  const refresh = localStorage.getItem('refreshToken');
  return api.get('/auth/newToken', {
    headers: { accessToken: refresh },
    isRefresh: true,
    noAuth: true
  });
}

// ========== 响应拦截 ===========
api.interceptors.response.use(
  res => res,
  async error => {
    const { response, config } = error;

    // 未登录（前端自己拦截返回的）
    if (error.code === 401 && error.message === "未登录") {
      clearTokensAndRedirect();
      return Promise.reject(error);
    }

    // 后端返回的 440 - RefreshToken 过期
    if (response && response.status === 440) {
      console.warn('刷新令牌已过期，需要重新登录');
      clearTokensAndRedirect();
      return Promise.reject(error);
    }

    // 后端返回的 401 - AccessToken 过期
    if (response && response.status === 401) {
      // 刷新请求也返回 401/440，则彻底失效
      if (config.isRefresh) {
        clearTokensAndRedirect();
        return Promise.reject(error);
      }

      // 检查是否有 refreshToken
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.warn('无刷新令牌，需要重新登录');
        clearTokensAndRedirect();
        return Promise.reject(error);
      }

      // 已经在刷新 → 加入等待队列
      if (isRefreshing) {
        return new Promise(resolve => {
          subscribe(token => {
            config.headers['accessToken'] = token;
            resolve(api(config));
          });
        });
      }

      // 启动刷新
      isRefreshing = true;
      try {
        const res = await refreshToken();
        
        // 刷新成功
        if (res.data.code === 200) {
          const newAccess = res.data.data.accessToken;
          const newRefresh = res.data.data.refreshToken;

          localStorage.setItem('accessToken', newAccess);
          localStorage.setItem('refreshToken', newRefresh);

          isRefreshing = false;
          notify(newAccess);

          config.headers['accessToken'] = newAccess;
          return api(config);
        } else {
          // 刷新接口返回非200状态
          throw new Error('刷新令牌失败');
        }

      } catch (e) {
        isRefreshing = false;
        
        // 区分是网络错误还是令牌失效
        if (e.response && e.response.status === 440) {
          console.warn('刷新令牌已失效');
        } else {
          console.error('刷新令牌请求失败:', e.message);
        }
        
        clearTokensAndRedirect();
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  }
);

export default {
  install(Vue) {
    Vue.prototype.$api = api;
  }
};

export { api };